import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import styled from "styled-components";
import { Spin, message, Tag } from "antd";
import { LeftOutlined, RightOutlined, FileTextOutlined, ProfileOutlined, UpOutlined, DownOutlined, UnorderedListOutlined } from "@ant-design/icons";

import EditorJS from "@editorjs/editorjs";
import { EDITOR_JS_TOOLS } from "../component/write/tools";
import { FreeTextContent } from "../component/freetext";

import { currentSlideAtom, navBarVisibleAtom, initialSlide } from "../atom";
import { getPostData, getImageList, getGrossImageList, viewPost, GrossImage, Photo } from "../api/postAPI";
import { Slide } from "../api/slideAPI";
import { getLoginUser, getUserCustomSetting, UserCustomSetting } from "../api/user";
import {
  Consult,
  getConsults,
  createConsult,
  updateConsult,
  deleteConsult,
  extractConsultPostInfo,
  isConsultPost,
} from "../api/consultAPI";

import { PostViewerSection, PostMediaTabPanel, Attatchment, PostReferences } from "../component/post";
import { ConsultInfoPanel, ConsultThread } from "../component/consultPost";
import { ViewerContext } from "./Viewer";

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
const noop = () => {};

const DEFAULT_USER_CUSTOM_SET: UserCustomSetting = {
  useScaleBar: true,
  useMagnificationBar: true,
  useCaptureButton: true,
  useViewerRotationSlider: true,
  useGammaSlider: true,
  useViewerThumbnail: true,
  useAnnotation: true,
  useICCButton: true,
  userGammaValue: 1,
  useInvertedZoom: false,
};

const ConsultPostPage = () => {
  const { id } = useParams<{ id?: string }>();
  const postId = id ? Number(id) : undefined;

  const navBarVisible = useRecoilValue(navBarVisibleAtom);
  const navBarOffset = useMemo(() => (navBarVisible ? 0 : 0), [navBarVisible]);

  // ── 데이터 ─────────────────────────────────────────────
  const [currentPostData, setCurrentPostData] = useState<any>(null);
  const [postReady, setPostReady] = useState(false);
  const [imageList, setImageList] = useState<Photo[]>([]);
  const [grossImageList, setGrossImageList] = useState<GrossImage[]>([]);
  const [slides, setSlides] = useState<Slide[]>([]);

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentImage, setCurrentImage] = useState<Photo | null>(null);
  const [currentGrossImage, setCurrentGrossImage] = useState<GrossImage | null>(null);
  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);
  const [isSlideFile, setIsSlideFile] = useState<boolean>(false);
  const [isGrossFile, setIsGrossFile] = useState<boolean>(false);

  const [currentUser, setCurrentUser] = useState<string>("");
  const [userCustomSet, setUserCustomSet] = useState<UserCustomSetting>(DEFAULT_USER_CUSTOM_SET);

  // ── 자문 ──────────────────────────────────────────────
  const [consults, setConsults] = useState<Consult[]>([]);
  const [consultLoading, setConsultLoading] = useState(false);

  // ── 레이아웃 (좌 에디터 / 중앙 뷰어 / 우 정보·자문) ───────
  const [editorOpen, setEditorOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [mediaListOpen, setMediaListOpen] = useState(true); // 하단 슬라이드/이미지 목록 열기·닫기
  const [editorWidth, setEditorWidth] = useState(26); // %
  const [rightWidth, setRightWidth] = useState(26); // %
  const [resizing, setResizing] = useState<null | "editor" | "right">(null);

  const editorRef = useRef<EditorJS | null>(null);

  const postInfo = useMemo(() => extractConsultPostInfo(currentPostData), [currentPostData]);
  const consultPost = useMemo(() => isConsultPost(currentPostData), [currentPostData]);

  // ── 최초 로드 ──────────────────────────────────────────
  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getPostData(postId);
        if (cancelled || !data) return;
        setCurrentPostData(data);

        const nextSlides: Slide[] = data?.slides || [];
        setSlides(nextSlides);

        const [images, grossImages] = await Promise.all([getImageList(postId), getGrossImageList(postId)]);
        if (cancelled) return;
        setImageList(images);
        setGrossImageList(grossImages);

        // 기본 선택: slide > image > gross 우선
        if (nextSlides.length > 0) {
          setIsSlideFile(true);
          setIsGrossFile(false);
          setCurrentSlide(nextSlides[0]);
        } else if (images.length > 0) {
          setIsSlideFile(false);
          setIsGrossFile(false);
          setCurrentImage(images[0]);
        } else if (grossImages.length > 0) {
          setIsSlideFile(false);
          setIsGrossFile(true);
          setCurrentGrossImage(grossImages[0]);
        } else {
          setIsSlideFile(true);
          setIsGrossFile(false);
        }
        setCurrentImage((prev) => prev ?? images[0] ?? null);
        setCurrentGrossImage((prev) => prev ?? grossImages[0] ?? null);
        setCurrentIndex(0);
      } catch (e) {
        console.error("Failed to load consult post:", e);
      }
    };
    load();
    viewPost(postId);
    return () => {
      cancelled = true;
    };
  }, [postId, setCurrentSlide]);

  useEffect(() => {
    if (currentPostData?.id) setPostReady(true);
  }, [currentPostData]);

  // 로그인 유저 / 뷰어 설정
  useEffect(() => {
    getLoginUser()
      .then((u: any) => {
        if (u) setCurrentUser(u.name || u.username || "");
      })
      .catch(() => {});
    getUserCustomSetting()
      .then((s) => {
        if (s) setUserCustomSet(s);
      })
      .catch(() => {});
  }, []);

  // ── 자문 로드 ──────────────────────────────────────────
  const loadConsults = useCallback(async (pid: number) => {
    setConsultLoading(true);
    try {
      setConsults(await getConsults(pid));
    } finally {
      setConsultLoading(false);
    }
  }, []);

  useEffect(() => {
    if (postId) loadConsults(postId);
  }, [postId, loadConsults]);

  const handleAddConsult = async (content: string) => {
    if (!postId) return;
    await createConsult(postId, content, currentUser || "익명");
    await loadConsults(postId);
    message.success("자문이 등록되었습니다.");
  };
  const handleEditConsult = async (consultId: number, content: string) => {
    if (!postId) return;
    await updateConsult(postId, consultId, content);
    await loadConsults(postId);
    message.success("자문이 수정되었습니다.");
  };
  const handleDeleteConsult = async (consultId: number) => {
    if (!postId) return;
    await deleteConsult(postId, consultId);
    await loadConsults(postId);
    message.success("자문이 삭제되었습니다.");
  };

  // ── 에디터(읽기전용) : 좌측 패널이 열려 있을 때만 마운트 ──
  useEffect(() => {
    if (!postReady || !editorOpen || !currentPostData) return;
    // FreeText 본문은 FreeTextContent 가 HTML 로 그린다
    if (currentPostData.contentFormat === "FREETEXT") return;
    let instance: EditorJS | null = null;
    let jsonData: any;
    try {
      jsonData = JSON.parse(currentPostData?.content);
    } catch {
      jsonData = undefined;
    }
    const raf = requestAnimationFrame(() => {
      instance = new EditorJS({
        holder: "consult-editorJS",
        readOnly: true,
        tools: EDITOR_JS_TOOLS(currentPostData.id),
        minHeight: 0,
        data: jsonData,
      });
      editorRef.current = instance;
    });
    return () => {
      cancelAnimationFrame(raf);
      const inst = instance;
      editorRef.current = null;
      if (inst && typeof inst.destroy === "function") {
        void inst.isReady
          .catch(() => undefined)
          .then(() => {
            try {
              inst.destroy();
            } catch {
              /* noop */
            }
          });
      }
    };
  }, [postReady, editorOpen, currentPostData]);

  // ── 미디어 선택/이동 (Case/Group 없음) ──────────────────
  const handleSlideSelect = (index: number) => {
    setIsSlideFile(true);
    setIsGrossFile(false);
    setCurrentSlide(slides[index] ?? initialSlide);
    setCurrentIndex(index);
  };
  const handleImageSelect = (index: number) => {
    setIsSlideFile(false);
    setIsGrossFile(false);
    setCurrentImage(imageList[index] ?? null);
    setCurrentIndex(index);
  };
  const handleGrossImageSelect = (index: number) => {
    setIsSlideFile(false);
    setIsGrossFile(true);
    setCurrentGrossImage(grossImageList[index] ?? null);
    setCurrentIndex(index);
  };
  const currentCount = isSlideFile ? slides.length : isGrossFile ? grossImageList.length : imageList.length;
  const selectByKind = (index: number) => {
    if (isSlideFile) handleSlideSelect(index);
    else if (isGrossFile) handleGrossImageSelect(index);
    else handleImageSelect(index);
  };
  const handlePrev = () => {
    if (currentIndex > 0) selectByKind(currentIndex - 1);
  };
  const handleNext = () => {
    if (currentIndex < currentCount - 1) selectByKind(currentIndex + 1);
  };

  // ── 레이아웃 리사이즈 ──────────────────────────────────
  useEffect(() => {
    if (!resizing) return;
    const onMove = (e: MouseEvent) => {
      const container = document.getElementById("consult-layout-container");
      if (!container) return;
      const rect = container.getBoundingClientRect();
      if (resizing === "editor") {
        const raw = ((e.clientX - rect.left) / rect.width) * 100;
        const maxByCenter = 70 - (rightOpen ? rightWidth : 0);
        setEditorWidth(clamp(Math.min(raw, maxByCenter), 15, 45));
      } else {
        const raw = ((rect.right - e.clientX) / rect.width) * 100;
        const maxByCenter = 70 - (editorOpen ? editorWidth : 0);
        setRightWidth(clamp(Math.min(raw, maxByCenter), 15, 45));
      }
    };
    const onUp = () => setResizing(null);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [resizing, editorWidth, rightWidth, editorOpen, rightOpen]);

  // ── 레이아웃 높이(뷰포트 채움) ─────────────────────────
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const [layoutHeight, setLayoutHeight] = useState<number | null>(null);
  useEffect(() => {
    const measure = () => {
      const el = layoutRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setLayoutHeight(Math.max(window.innerHeight - top - 8, 360));
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [navBarOffset, postReady]);

  if (!currentPostData) {
    return (
      <LoadingWrap $navBarOffset={navBarOffset}>
        <Spin size="large" tip="불러오는 중..." />
      </LoadingWrap>
    );
  }

  return (
    <Page $navBarOffset={navBarOffset}>
      <Layout id="consult-layout-container" ref={layoutRef} style={layoutHeight ? { height: `${layoutHeight}px` } : undefined}>
        {/* ── 좌: 본문(에디터) ── */}
        {editorOpen ? (
          <SidePanel style={{ flexBasis: `${editorWidth}%`, maxWidth: `${editorWidth}%` }}>
            <PanelHeader>
              <PanelTitle>
                <FileTextOutlined /> 본문
              </PanelTitle>
              <CollapseBtn type="button" title="접기" onClick={() => setEditorOpen(false)}>
                <LeftOutlined />
              </CollapseBtn>
            </PanelHeader>
            <PanelBody>
              <PostTitle>{currentPostData.title || "제목 없음"}</PostTitle>
              <MetaLine>
                작성자: {currentPostData.writer || "알 수 없음"}
                {consultPost && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    자문 게시글
                  </Tag>
                )}
              </MetaLine>
              {currentPostData.contentFormat === "FREETEXT" ? (
                <EditorHolder>
                  <FreeTextContent html={currentPostData.content} htmlEn={currentPostData.contentEn} />
                </EditorHolder>
              ) : (
                <EditorHolder id="consult-editorJS" />
              )}
              <AttachmentWrap>
                <PostReferences postId={currentPostData.id} referenceText={currentPostData.referenceText} compact />
                <Attatchment postId={currentPostData.id} isDeleteButtonVisible={false} />
              </AttachmentWrap>
            </PanelBody>
          </SidePanel>
        ) : (
          <CollapsedTab type="button" onClick={() => setEditorOpen(true)} title="본문 열기">
            <FileTextOutlined />
            <TabLabel>본문</TabLabel>
            <RightOutlined />
          </CollapsedTab>
        )}

        {editorOpen && (
          <Divider $active={resizing === "editor"} onMouseDown={() => setResizing("editor")}>
            <DividerHandle />
          </Divider>
        )}

        {/* ── 중앙: 뷰어 ── */}
        <ViewerContext.Provider value={{ userCustomSet, setUserCustomSet }}>
          <CenterPanel>
            <PostViewerSection
              imageList={imageList}
              slides={slides}
              grossImageList={grossImageList}
              currentIndex={currentIndex}
              isSlideFile={isSlideFile}
              isGrossFile={isGrossFile}
              currentImage={currentImage}
              currentSlide={currentSlide}
              currentGrossImage={currentGrossImage}
              currentPost={currentPostData}
              hasEditAuth={false}
              activeCaseId={null}
              layoutMode="horizontal"
              hideResizeDivider
              onPrev={handlePrev}
              onNext={handleNext}
              onEditImage={noop}
              onEditGrossImage={noop}
            />

            {/* 하단 목록: 높이 조절 없이 열기/닫기만 */}
            <MediaListToggle type="button" onClick={() => setMediaListOpen((o) => !o)}>
              <ToggleLeft>
                <UnorderedListOutlined />
                <span>슬라이드 · 이미지 목록</span>
              </ToggleLeft>
              {mediaListOpen ? <DownOutlined /> : <UpOutlined />}
            </MediaListToggle>

            {mediaListOpen && (
              <MediaTabWrap>
                <PostMediaTabPanel
                  slides={slides}
                  imageList={imageList}
                  grossImageList={grossImageList}
                  currentIndex={currentIndex}
                  isSlideFile={isSlideFile}
                  isGrossFile={isGrossFile}
                  hasEditAuth={false}
                  onSlideSelect={handleSlideSelect}
                  onImageSelect={handleImageSelect}
                  onGrossImageSelect={handleGrossImageSelect}
                  onOpenSlideEditModal={noop}
                  onOpenImageEditModal={noop}
                  onOpenGrossImageEditModal={noop}
                  onShowAddSlideModal={noop}
                  onShowAddImageModal={noop}
                  onShowAddGrossImageModal={noop}
                  slideOnlyMode={false}
                />
              </MediaTabWrap>
            )}
          </CenterPanel>
        </ViewerContext.Provider>

        {rightOpen && (
          <Divider $active={resizing === "right"} onMouseDown={() => setResizing("right")}>
            <DividerHandle />
          </Divider>
        )}

        {/* ── 우: Post 정보 + 자문 ── */}
        {rightOpen ? (
          <SidePanel style={{ flexBasis: `${rightWidth}%`, maxWidth: `${rightWidth}%` }}>
            <PanelHeader>
              <PanelTitle>
                <ProfileOutlined /> 정보 · 자문
              </PanelTitle>
              <CollapseBtn type="button" title="접기" onClick={() => setRightOpen(false)}>
                <RightOutlined />
              </CollapseBtn>
            </PanelHeader>
            <PanelBody>
              <ConsultInfoPanel info={postInfo} />
              <ConsultDivider />
              <ConsultThread
                consults={consults}
                currentUser={currentUser}
                loading={consultLoading}
                onAdd={handleAddConsult}
                onEdit={handleEditConsult}
                onDelete={handleDeleteConsult}
              />
            </PanelBody>
          </SidePanel>
        ) : (
          <CollapsedTab type="button" onClick={() => setRightOpen(true)} title="정보·자문 열기">
            <LeftOutlined />
            <TabLabel>정보 · 자문</TabLabel>
            <ProfileOutlined />
          </CollapsedTab>
        )}
      </Layout>
    </Page>
  );
};

export default ConsultPostPage;

/* ============================ styled ============================ */

const Page = styled.div<{ $navBarOffset: number }>`
  max-width: 100%;
  overflow-x: hidden;
  padding-top: ${({ $navBarOffset }) => $navBarOffset}px;
  background: #f5f6f8;
`;

const LoadingWrap = styled.div<{ $navBarOffset: number }>`
  padding-top: ${({ $navBarOffset }) => $navBarOffset}px;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Layout = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  gap: 0;
  padding: 10px;
  box-sizing: border-box;
`;

const SidePanel = styled.div`
  flex: 0 0 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e2e3a;
`;

const CollapseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #8c8c8c;
  padding: 4px 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s;

  &:hover {
    color: #1890ff;
    background: #eef5ff;
  }
`;

const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
`;

const CenterPanel = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin: 0 0;
`;

const MediaTabWrap = styled.div`
  flex-shrink: 0;
`;

const MediaListToggle = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-top: 1px solid #e8e8e8;
  background: #fafafa;
  cursor: pointer;
  color: #595959;
  font-size: 0.85rem;
  font-weight: 600;
  transition: background 0.15s;

  &:hover {
    background: #f0f5ff;
    color: #1890ff;
  }
`;

const ToggleLeft = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Divider = styled.div<{ $active: boolean }>`
  flex: 0 0 10px;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  position: relative;

  &:hover > div {
    background: #1890ff;
    opacity: 1;
  }
  ${({ $active }) => $active && `> div { background: #1890ff; opacity: 1; }`}
`;

const DividerHandle = styled.div`
  width: 3px;
  height: 44px;
  border-radius: 2px;
  background: #d9d9d9;
  opacity: 0.7;
  transition: all 0.15s;
`;

const CollapsedTab = styled.button`
  flex: 0 0 40px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 0;
  color: #595959;
  transition: all 0.15s;

  &:hover {
    color: #1890ff;
    border-color: #91caff;
    background: #f7fbff;
  }
`;

const TabLabel = styled.span`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  letter-spacing: 1px;
  font-size: 0.85rem;
  font-weight: 600;
`;

const PostTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e2e3a;
  margin: 0 0 6px;
  word-break: break-word;
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #8c8c8c;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const EditorHolder = styled.div`
  min-width: 0;
  max-width: 100%;
`;

const AttachmentWrap = styled.div`
  margin-top: 20px;
`;

const ConsultDivider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 18px 0;
`;
